import { QueryClient, dehydrate } from '@tanstack/react-query'
import { BadRequest } from '@tsed/exceptions'
import type { MDXRemoteSerializeResult } from 'next-mdx-remote'
import { serialize } from 'next-mdx-remote/serialize'
import { QueryKeys } from '@/lib/react-query'
import { useFragment } from 'gql'
import queryArticles from 'query/common/articles'
import queryPetParentsCornerPage from 'query/pet-parents-corner'
import { pageSectionsFragment, blockMdxFragment } from 'query/single-page/fragment'
import { ARTICLES_LIMIT_LCDH_PAGE } from '../../../../const'
import { PetParentsCornerPage } from '../pages'
import { petParentsCornerConfig } from '../pet-parents-corner.config'
import type TPetParentsCorner from '../types'

export default function PetParentsCornerPageRoute({ mdxSources, ...rest }: TPetParentsCorner) {
  return <PetParentsCornerPage {...rest} mdxSources={mdxSources} />
}

export const getPetParentsCornerPageStaticProps = async (locale?: string) => {
  if (locale === undefined) {
    throw new BadRequest('locale is missing')
  }

  const { i18nNamespaces } = petParentsCornerConfig

  const queryClient = new QueryClient()
  const page = await queryClient.fetchQuery([QueryKeys.GetPetParentsCornerPage], () =>
    queryPetParentsCornerPage(locale),
  )

  const articles = await queryClient.fetchQuery([QueryKeys.GetPetParentsCornerArticles], () =>
    queryArticles(locale, ARTICLES_LIMIT_LCDH_PAGE),
  )

  if (page.pages === undefined || page.pages.length === 0 || articles.articles === undefined) {
    return {
      notFound: true,
      i18nNamespaces,
      locale,
    }
  }

  const MDXSections = page.pages?.[0]?.translations?.[0]?.sections?.filter((section) => {
    const sectionTyped = useFragment(pageSectionsFragment, section)
    return sectionTyped?.item?.type === 'block_mdx'
  })

  const mdxSources: Array<MDXRemoteSerializeResult<Record<string, unknown>, Record<string, unknown>>> = []

  MDXSections?.forEach(async (section) => {
    const sectionTyped = useFragment(pageSectionsFragment, section)
    if (sectionTyped?.item?.type === 'block_mdx') {
      const mdxBlock = useFragment(blockMdxFragment, sectionTyped.item)
      const mdxBlockSource = await serialize(mdxBlock?.content ?? '')
      mdxSources.push(mdxBlockSource)
    }
  })

  const seo = {
    seo: page?.pages?.[0]?.translations?.[0]?.seo,
    pageTitle: page?.pages?.[0]?.translations?.[0]?.title,
  }

  return {
    i18nNamespaces,
    locale,
    props: {
      dehydratedState: dehydrate(queryClient),
      mdxSources,
      seo,
    },
  }
}
